import React, { useState } from 'react';
import { Star, ChevronLeft, ChevronRight, Quote } from 'lucide-react';

const testimonials = [
  {
    name: 'Sarah Mitchell',
    role: 'CTO, FinFlow Technologies',
    avatar: 'SM',
    stars: 5,
    text: 'Nuage transformed our legacy monolith into a cloud-native microservices platform in 12 weeks. Their team understood our business constraints as well as our technical ones — a rare combination.',
  },
  {
    name: 'Rahul Mehta',
    role: 'Founder, MedConnect',
    avatar: 'RM',
    stars: 5,
    text: 'The Flutter app they delivered was more polished than apps built by teams 10x their size. The AI-powered features they suggested weren\'t in scope — they just cared enough to propose them. That says everything.',
  },
  {
    name: 'Priya Sharma',
    role: 'Head of Engineering, RetailPulse',
    avatar: 'PS',
    stars: 5,
    text: 'We brought Nuage in to fix our CI/CD chaos. Within 2 weeks, deploys that took 4 hours were down to 8 minutes. Their DevOps engineers knew exactly what to do and why.',
  },
  {
    name: 'James O\'Brien',
    role: 'CEO, LexAI',
    avatar: 'JO',
    stars: 5,
    text: 'Our RAG document intelligence system processes 10,000 legal documents a day and our lawyers love it. Nuage built something we thought would take a year — in 3 months.',
  },
  {
    name: 'Anjali Krishnan',
    role: 'Product Director, EduTech Global',
    avatar: 'AK',
    stars: 5,
    text: 'From day one, Nuage operated like a true engineering partner — not a vendor. Weekly demos, transparent communication, and genuine investment in our mission. Highly recommend.',
  },
];

export default function Testimonials() {
  const [idx, setIdx] = useState(0);
  const prev = () => setIdx(i => (i - 1 + testimonials.length) % testimonials.length);
  const next = () => setIdx(i => (i + 1) % testimonials.length);
  const t = testimonials[idx];

  return (
    <section id="testimonials" style={{ padding: '7rem 0', background: 'var(--bg-secondary)', position: 'relative', overflow: 'hidden' }}>
      <div className="glow-orb" style={{
        width: '500px', height: '500px',
        background: 'radial-gradient(circle, rgba(37,138,203,0.08) 0%, transparent 70%)',
        top: '50%', left: '50%', transform: 'translate(-50%,-50%)',
      }} />

      <div className="container">
        <div style={{ textAlign: 'center', marginBottom: '4rem' }}>
          <div className="section-label" style={{ margin: '0 auto 1.5rem' }}>
            <span>◆</span> Testimonials
          </div>
          <h2 className="section-heading" style={{ margin: '0 auto 1rem' }}>
            What our <span className="gradient-text">clients say</span>
          </h2>
        </div>

        {/* Main testimonial */}
        <div style={{ maxWidth: '780px', margin: '0 auto', textAlign: 'center' }}>
          <div className="glass-card" style={{ padding: '3rem', position: 'relative' }}>
            {/* Quote icon */}
            <div style={{
              position: 'absolute', top: '1.5rem', left: '1.5rem',
              color: 'var(--accent)', opacity: 0.3,
            }}>
              <Quote size={40} />
            </div>

            {/* Stars */}
            <div style={{ display: 'flex', justifyContent: 'center', gap: '0.2rem', marginBottom: '1.5rem' }}>
              {[...Array(t.stars)].map((_, i) => (
                <Star key={i} size={18} fill="#258ACB" color="#258ACB" />
              ))}
            </div>

            <p style={{
              fontSize: '1.1rem', lineHeight: 1.8,
              color: 'var(--text-primary)', marginBottom: '2rem',
              fontStyle: 'italic',
            }}>"{t.text}"</p>

            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '1rem' }}>
              <div style={{
                width: '48px', height: '48px', borderRadius: '50%',
                background: 'linear-gradient(135deg, var(--accent), var(--accent-dark))',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: '0.9rem',
                color: 'white',
              }}>{t.avatar}</div>
              <div style={{ textAlign: 'left' }}>
                <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: '0.95rem' }}>{t.name}</div>
                <div style={{ fontSize: '0.82rem', color: 'var(--text-muted)' }}>{t.role}</div>
              </div>
            </div>
          </div>

          {/* Controls */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '1rem', marginTop: '2rem' }}>
            <button onClick={prev} style={{
              width: '44px', height: '44px', borderRadius: '50%',
              background: 'transparent', border: '1px solid var(--border)',
              cursor: 'pointer', color: 'var(--text-primary)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              transition: 'all 0.2s',
            }}
              onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--accent)'}
              onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border)'}
            ><ChevronLeft size={18} /></button>

            <div style={{ display: 'flex', gap: '0.4rem' }}>
              {testimonials.map((_, i) => (
                <button key={i} onClick={() => setIdx(i)} style={{
                  width: i === idx ? '24px' : '8px', height: '8px',
                  borderRadius: '4px', border: 'none', cursor: 'pointer',
                  background: i === idx ? 'var(--accent)' : 'var(--text-muted)',
                  transition: 'all 0.3s',
                }} />
              ))}
            </div>

            <button onClick={next} style={{
              width: '44px', height: '44px', borderRadius: '50%',
              background: 'transparent', border: '1px solid var(--border)',
              cursor: 'pointer', color: 'var(--text-primary)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              transition: 'all 0.2s',
            }}
              onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--accent)'}
              onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border)'}
            ><ChevronRight size={18} /></button>
          </div>
        </div>
      </div>
    </section>
  );
}
